import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { money, shortDate } from "@/lib/format";

interface PQ { number: string; version: number; title: string | null; status: string; upfrontTotal: number; monthlyTotal: number; notes: string | null; validUntil: string | null; expired: boolean; accountName: string; acceptedAt: string | null; acceptedByName: string | null; lines: { name: string; description: string | null; kind: string; quantity: number; unitPrice: number; isAddOn: boolean }[] }

export function PublicQuotePage() {
  const { token } = useParams();
  const [q, setQ] = useState<PQ | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState({ name: "", email: "", agree: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<"accepted" | "declined" | null>(null);
  useEffect(() => { fetch(`/api/public/quote/${token}`).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error); setQ(d); }).catch((e) => setErr(e.message)); }, [token]);
  const act = async (kind: "accept" | "decline") => {
    setBusy(true);
    try {
      const r = await fetch(`/api/public/quote/${token}/${kind}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(kind === "accept" ? f : { reason: "" }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? "Something went wrong");
      setDone(kind === "accept" ? "accepted" : "declined");
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="public">
      <div className="wrap">
        <div className="brand"><span style={{ display: "inline-flex", alignItems: "center", background: "#070709", borderRadius: 8, padding: "8px 14px" }}><img src="/assets/hixonops-wordmark.png" alt="HixonOps" style={{ height: 24 }} /></span></div>
        <div className="sheet">
          {err && !q && <div className="err">{err}</div>}
          {q && (
            <>
              <h1>{q.title ?? "Quote"} <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}>{q.number} · v{q.version}</span></h1>
              <p className="muted">Prepared for {q.accountName}{q.validUntil ? ` · valid until ${shortDate(q.validUntil)}` : ""}</p>
              <table>
                <thead><tr><th>Item</th><th style={{ textAlign: "right" }}>Qty</th><th style={{ textAlign: "right" }}>Price</th></tr></thead>
                <tbody>
                  {q.lines.map((l, i) => <tr key={i}><td>{l.name}{l.isAddOn ? <span className="muted"> (add-on)</span> : ""}{l.description && <div className="muted" style={{ fontSize: 13 }}>{l.description}</div>}</td><td style={{ textAlign: "right" }}>{l.quantity}</td><td style={{ textAlign: "right", fontFamily: "var(--mono)" }}>{money(l.quantity * l.unitPrice, { cents: true })}{l.kind === "recurring" ? "/mo" : ""}</td></tr>)}
                </tbody>
              </table>
              <div className="total"><span>Upfront</span><span style={{ fontFamily: "var(--mono)" }}>{money(q.upfrontTotal, { cents: true })}</span></div>
              <div className="total"><span>Monthly</span><span style={{ fontFamily: "var(--mono)" }}>{money(q.monthlyTotal, { cents: true })}/mo</span></div>
              {q.notes && <p className="muted" style={{ marginTop: 16, whiteSpace: "pre-wrap" }}>{q.notes}</p>}
              <div style={{ marginTop: 24 }}>
                {done === "accepted" || q.status === "accepted" ? <div className="ok">Accepted{q.acceptedByName ? ` by ${q.acceptedByName}` : ""}. Thank you. We will send the agreement and the deposit invoice next.</div>
                : done === "declined" || q.status === "declined" ? <div className="notice">Declined. Reply to the email if you want to revisit this.</div>
                : q.expired ? <div className="notice">This quote has expired. Reply to the email and we will refresh it.</div>
                : q.status === "superseded" ? <div className="notice">A newer version of this quote was sent. Please use the latest link.</div>
                : (
                  <div className="col gap-12" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {err && <div className="err">{err}</div>}
                    <div className="form-grid">
                      <div className="field"><label>Your name</label><input className="input" value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} /></div>
                      <div className="field"><label>Your email</label><input className="input" type="email" value={f.email} onChange={(e) => setF((x) => ({ ...x, email: e.target.value }))} /></div>
                    </div>
                    <label className="checkrow"><input type="checkbox" checked={f.agree} onChange={(e) => setF((x) => ({ ...x, agree: e.target.checked }))} /><span>I accept this quote on behalf of {q.accountName}. HixonOps will follow up with a written agreement and a deposit invoice for {money(q.upfrontTotal / 2)}.</span></label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn primary" disabled={busy || !f.name || !f.email || !f.agree} onClick={() => act("accept")}>Accept {q.number}</button>
                      <button className="btn" disabled={busy} onClick={() => act("decline")}>Decline</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 16 }}>HixonOps · Missoula, MT · Questions? Reply to the email that brought you here.</p>
      </div>
    </div>
  );
}
